import {Component, computed, inject, resource, signal, ViewChild} from '@angular/core';
import {saveAs} from 'file-saver';
import {take} from 'rxjs/operators';
import {DxDataGridComponent} from 'devextreme-angular';
import {ButtonThemeEnum} from '../../shared/ui/button/button-theme-enum';
import {UtilsService} from '../../core/utils/utils.service';
import {DateLevelEnum} from "../../shared/date/picker/date-level-enum";
import {StocksService} from '../../core/services/gestion-stock/stocks.service';
import {VariationStockML} from '../../core/models/gestion-stock/variation-stock.model';
import {VariationStockStatutExportEnum} from '../../core/enums/gestion-stock/variation-stock-statut-export.enum';
import {EditorPreparingEvent, SelectionChangedEvent} from "devextreme/ui/data_grid";
import {ValueChangedEvent} from "devextreme/ui/check_box";
import {IconThemeEnum} from "../../shared/ui/icon/icon-theme-enum";

function buildDefaultPeriode(): Date[] {
  const now = new Date();
  return [
    new Date(now.getFullYear(), now.getMonth() - 1, 0),
    new Date(now.getFullYear(), now.getMonth(), 0)
  ];
}

@Component({
  selector: 'yo-variations-stocks',
  templateUrl: './variations-stocks.component.html',
  styleUrls: ['./variations-stocks.component.scss'],
  standalone: false
})
export class VariationsStocksComponent {

  public readonly ButtonThemeEnum = ButtonThemeEnum;
  public readonly IconThemeEnum = IconThemeEnum;
  public readonly DateLevelEnum = DateLevelEnum;
  public readonly VariationStockStatutExportEnum = VariationStockStatutExportEnum;

  @ViewChild('grid') grid: DxDataGridComponent;

  private readonly utils = inject(UtilsService);
  private readonly stocksSvc = inject(StocksService);

  readonly periodeSelected = signal<Date[]>(buildDefaultPeriode());
  readonly selectedVariationList = signal<VariationStockML[]>([]);

  readonly compteComptableHeaderFilter = {
    dataSource: (options: any) => {
      options.dataSource.postProcess = (results: any[]) => results.map((item: any) => ({
        ...item,
        text: item.text || 'Non renseigné'
      }));
    }
  };

  readonly variationsResource = resource<VariationStockML[], { dateDebut: Date | null; dateFin: Date | null }>({
    params: () => ({dateDebut: this.periodeSelected()?.[0] ?? null, dateFin: this.periodeSelected()?.[1] ?? null}),
    loader: async ({params}) => {
      try {
        const response: any = await this.stocksSvc.getVariationsStock(params.dateDebut, params.dateFin).pipe(take(1)).toPromise();
        return response?.one ?? [];
      } catch (err) {
        this.utils.handleError(err);
        return [];
      }
    }
  });

  onPeriodeChanged(dates: Date[]): void {
    this.periodeSelected.set(dates ?? [null, null]);
    this.selectedVariationList.set([]);
    this.grid.instance.clearSelection();
  }

  isRowSelectable = (row: VariationStockML): boolean => {
    return !!(row.dateDebut && row.dateFin && row.uniteProductionId && row.compteComptableId);
  };

  readonly selectableList = computed(() =>
    (this.variationsResource.value() ?? []).filter(row => this.isRowSelectable(row))
  );

  /** true = toutes les exportables cochées, false = aucune sélectionnée (ou aucune exportable), undefined = indéterminé */
  readonly selectAllValue = computed(() => {
    const selectableCount = this.selectableList().length;
    const selectedCount = this.selectedVariationList().length;
    if (selectableCount === 0 || selectedCount === 0) return false;
    if (selectedCount >= selectableCount) return true;
    return undefined;
  });

  onSelectAllChanged($event: ValueChangedEvent): void {
    if (!$event.event) return;
    if ($event.value) {
      this.grid.instance.selectRows(this.selectableList(), false);
    } else {
      this.grid.instance.deselectAll();
    }
  }

  private variationKey = (v: VariationStockML): string =>
    `${v.uniteProductionId}_${v.dateDebut}_${v.dateFin}_${v.compteComptableId}`;

  readonly canMarkAExporter = computed(() => this.selectedVariationList().length > 0);

  readonly aExporterList = computed(() =>
    (this.variationsResource.value() ?? []).filter(row => row.statutExport === VariationStockStatutExportEnum.A_EXPORTER)
  );

  readonly canExport = computed(() => this.selectedVariationList().length > 0 || this.aExporterList().length > 0);

  onMarkAExporter(): void {
    this.stocksSvc.markVariationAExporter(this.selectedVariationList()).subscribe(() => {
      this.applyStatutAndResetSelection(VariationStockStatutExportEnum.A_EXPORTER);
    });
  }

  onExport(): void {
    const toExport = this.selectedVariationList().length > 0 ? this.selectedVariationList() : this.aExporterList();

    this.stocksSvc.exportVariationsStock(toExport).subscribe({
      next: response => {
        if (!response.body) return;
        const disposition = response.headers.get('Content-Disposition') ?? '';
        const match = disposition.match(/filename=([^;]+);?/);
        const filename = match[1];
        saveAs(response.body, filename);
        this.applyStatutAndResetSelection(VariationStockStatutExportEnum.EXPORTE, toExport);
      },
      error: err => this.utils.handleError(err)
    });
  }

  private applyStatutAndResetSelection(
    statutExport: VariationStockStatutExportEnum,
    concernedList: VariationStockML[] = this.selectedVariationList()
  ): void {
    const concernedKeys = new Set(concernedList.map(this.variationKey));
    this.variationsResource.update(current => (current ?? []).map(v =>
      concernedKeys.has(this.variationKey(v)) ? {...v, statutExport} : v
    ));
    this.selectedVariationList.set([]);
    this.grid.instance.clearSelection();
  }

  onEditorPreparing($event: EditorPreparingEvent<VariationStockML>): void {
    if ($event.parentType === 'dataRow' && !$event.dataField && $event.row?.rowType === 'data' && !this.isRowSelectable($event.row.data)) {
      $event.editorOptions.disabled = true;
      $event.editorOptions.visible = false;
    }
  }

  onSelectionChanged($event: SelectionChangedEvent<VariationStockML>): void {
    const disallowedList = $event.selectedRowsData.filter(row => !this.isRowSelectable(row));
    if (disallowedList.length > 0) {
      this.grid.instance.deselectRows(disallowedList);
      return;
    }
    this.selectedVariationList.set($event.selectedRowsData);
  }
}
